<template>
  <div class="h-screen flex flex-col" style="background: var(--background-primary);">
    <!-- Top Navigation Bar - Asher Dark Theme -->
    <div class="px-6 py-4 flex items-center justify-between border-b"
         style="background: var(--primary-dark); color: var(--primary-white); border-color: var(--viz-6);">
      <div class="flex items-center gap-4">
        <div class="flex items-center gap-3">
          <div class="px-3 py-2 rounded-lg bg-white">
            <img
              src="/AsherInformatics_logo_name.png"
              alt="Asher Informatics"
              class="h-8"
            />
          </div>
          <div class="border-l pl-3" style="border-color: var(--viz-6);">
            <h1 class="text-xl font-bold" style="color: var(--primary-white);">
              AI Governance Studio
            </h1>
            <span class="text-xs font-normal" style="color: var(--sage-green);">
              Explorer Mode
            </span>
          </div>
        </div>
      </div>

      <div class="flex gap-4">
        <Button
          label="Home"
          icon="pi pi-home"
          severity="secondary"
          @click="$router.push('/')"
          class="domain-button"
          title="Back to Welcome"
        />

        <Button
          label="Blueprint"
          icon="pi pi-sitemap"
          severity="secondary"
          @click="$router.push('/blueprint')"
          class="domain-button"
          title="Framework Architecture Blueprint"
        />

        <Button
          label="Explore"
          icon="pi pi-compass"
          severity="secondary"
          @click="$router.push('/explore')"
          class="domain-button"
          title="Ask questions about AI governance"
        />

        <!-- Domain switchers: Framework / Policy / Process.
             Policy & Process re-enabled to surface the full CHAR model in Explorer
             (they had been hidden to avoid "showing too much"). -->
        <Button
          label="Framework"
          icon="pi pi-book"
          :severity="explorerStore.activeDomain === 'framework' ? 'warning' : 'secondary'"
          @click="explorerStore.switchDomain('framework')"
          class="domain-button"
        />

        <Button
          label="Policy"
          icon="pi pi-shield"
          :severity="explorerStore.activeDomain === 'policy' ? 'warning' : 'secondary'"
          @click="explorerStore.switchDomain('policy')"
          class="domain-button"
        />

        <Button
          label="Process"
          icon="pi pi-cog"
          :severity="explorerStore.activeDomain === 'process' ? 'warning' : 'secondary'"
          @click="explorerStore.switchDomain('process')"
          class="domain-button"
        />

        <div class="w-px bg-gray-600 mx-2"></div>

        <Button
          label="Crosswalk"
          icon="pi pi-table"
          severity="secondary"
          @click="$router.push('/crosswalk')"
          class="domain-button"
          title="Framework Crosswalk - See how AshMatics maps to regulatory frameworks"
        />
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Left Navigation Sidebar -->
      <aside class="w-[280px] flex-shrink-0 border-r"
             style="border-color: var(--border-light); background: var(--background-secondary);">
        <NavigationTree
          :domain="explorerStore.activeDomain"
          :sections="explorerStore.currentDomainSections"
          :activeSection="explorerStore.activeSection"
          @section-selected="handleSectionSelected"
        />
      </aside>

      <!-- Main Content Panel -->
      <main class="flex-1 flex overflow-hidden" style="background: var(--background-primary);">
        <div class="flex-1 flex flex-col overflow-hidden">
          <!-- Breadcrumb Navigation -->
          <BreadcrumbNav v-if="explorerStore.activeSection" />

          <!-- Content Viewer -->
          <div class="flex-1 overflow-hidden">
            <ContentViewer
              :section="explorerStore.activeSection"
              :domain="explorerStore.activeDomain"
              @active-content-changed="handleActiveContentChanged"
            />
          </div>
        </div>

        <!-- Context Panel (Right Sidebar) -->
        <ContextPanel
          v-if="explorerStore.activeSection"
          :section="explorerStore.activeSection"
          :markdown-content="activeMarkdownContent"
          :show-toc="showToc"
        />
      </main>
    </div>

    <!-- Related-artifact peek modal (opened from Related Processes / Related Policies cards) -->
    <RelatedArtifactModal />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import Button from 'primevue/button';
import NavigationTree from './NavigationTree.vue';
import ContentViewer from './ContentViewer.vue';
import BreadcrumbNav from './BreadcrumbNav.vue';
import ContextPanel from './ContextPanel.vue';
import RelatedArtifactModal from './RelatedArtifactModal.vue';
import { useExplorerStore } from '@/stores/explorerStore';
import type { Section } from '@/types/ashmatics';

const explorerStore = useExplorerStore();
const activeMarkdownContent = ref<string>('');
const showToc = ref<boolean>(true);

const handleSectionSelected = (section: Section) => {
  explorerStore.setActiveSection(section);
};

const handleActiveContentChanged = (content: string, shouldShowToc: boolean = true) => {
  activeMarkdownContent.value = content;
  showToc.value = shouldShowToc;
};

// Computed property to get initial markdown content
const currentMarkdown = computed(() => {
  const section = explorerStore.activeSection;
  if (!section) return '';

  // Try tab_contents first (Framework domain with tabs)
  if (section.tab_contents) {
    // Default to 'overview' tab or first available tab
    return section.tab_contents['overview'] || Object.values(section.tab_contents)[0] || '';
  }

  // Fallback to markdown_content
  return section.markdown_content || '';
});

// Watch for section changes to update markdown content
import { watch } from 'vue';
watch(() => explorerStore.activeSection, (newSection) => {
  if (newSection) {
    if (newSection.tab_contents) {
      activeMarkdownContent.value = newSection.tab_contents['overview'] || Object.values(newSection.tab_contents)[0] || '';
    } else {
      activeMarkdownContent.value = newSection.markdown_content || '';
    }
  } else {
    activeMarkdownContent.value = '';
  }
}, { immediate: true });
</script>

<style scoped>
/* Add spacing between icon and label in domain buttons */
.domain-button :deep(.p-button-icon) {
  margin-right: 0.75rem;
}
</style>
